import * as Popover from '@radix-ui/react-popover';
import { endOfDay, format, startOfDay, subDays } from 'date-fns';
import { CalendarDays, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import type { DateRange } from 'react-day-picker';

import { Button } from '@/components/ui/button';
import type { AnalyticsUrlState } from '@/lib/urlState';
import { normalizeWindow } from '@/lib/urlState';

import 'react-day-picker/style.css';

type DateRangeControlsProperties = {
  onChange: (value: { end: Date; start: Date }) => void;
  state: AnalyticsUrlState;
};

const quickRanges = [
  { label: 'Today', value: 'today' },
  { label: '7 days', value: '7d' },
  { label: '30 days', value: '30d' },
] as const;
const quickRangeDays = { '30d': 30, '7d': 7, today: 0 } as const;

export const DateRangeControls = ({ onChange, state }: DateRangeControlsProperties) => {
  const [open, setOpen] = useState(false);

  const selectedRange: DateRange = {
    from: state.start,
    to: state.end,
  };

  const selectQuickRange = (range: (typeof quickRanges)[number]['value']) => {
    const today = new Date();
    onChange(normalizeWindow(startOfDay(subDays(today, quickRangeDays[range])), endOfDay(today)));
    setOpen(false);
  };

  const rangeLabel = `${format(state.start, 'MMM d')} – ${format(state.end, 'MMM d, yyyy')}`;

  return (
    <Popover.Root onOpenChange={setOpen} open={open}>
      <Popover.Trigger asChild>
        <Button aria-label={`Date range: ${rangeLabel}`} className="justify-between" variant="secondary">
          <CalendarDays aria-hidden="true" className="text-muted size-4" />
          <span className="numeric">{rangeLabel}</span>
          <ChevronDown aria-hidden="true" className="text-muted size-4" />
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          className="border-line bg-raised text-ink shadow-raised z-50 w-[min(94vw,40rem)] rounded-lg border p-4"
          sideOffset={8}
        >
          <div className="grid gap-4 md:grid-cols-[auto_1fr]">
            <div>
              <p className="text-muted mb-2 text-xs font-semibold tracking-wide uppercase">Quick range</p>
              <div className="grid grid-cols-3 gap-2 md:grid-cols-1">
                {quickRanges.map((range) => (
                  <Button key={range.value} onClick={() => selectQuickRange(range.value)} variant="ghost">
                    {range.label}
                  </Button>
                ))}
              </div>
            </div>
            <DayPicker
              mode="range"
              onSelect={(range) => {
                if (range?.from && range.to) {
                  onChange(normalizeWindow(startOfDay(range.from), endOfDay(range.to)));
                  setOpen(false);
                }
              }}
              selected={selectedRange}
            />
          </div>
          <p className="border-line text-muted mt-4 border-t pt-3 text-xs">Ranges use calendar dates in your local timezone.</p>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
